import { Component, inject, input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { cloneDeep, isEqual } from 'lodash';
import { DnDidRange, MicrosoftDialPlanRange } from '../../../shared/models/generated/ziroModels';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { Observable, of, Subscription } from 'rxjs';
import { ZiroTextareaConfig } from '../../../forms/fields/textarea/ziro-textarea.component';
import { TranslateService } from '@ngx-translate/core';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { ZiroRadioConfig } from '../../../forms/fields/radio/ziro-radio.component';
import {
  ExtensionRangesDisplayFormComponent,
  RangeDisplayFormModel,
} from '../../../forms/fields/extension-ranges/extension-ranges-display-form/extension-ranges-display-form.component';
import { ExtensionRangesOptionalValidators } from '../../../forms/fields/extension-ranges/ziro-extension-range-models';
import { RangeService } from '../../../shared/services/range.service';
import { CANADIAN_AREA_CODES_2024 } from '../../../shared/models/canadian-area-codes';

enum EnterByType {
  TEXT = 'Text',
  RANGES = 'Ranges',
}

export interface CheckNumberPortabilityComponentPayload {
  enterByType: 'Text' | 'Ranges';
  bulkNumbers?: string;
  dialPlanRangesJson?: MicrosoftDialPlanRange[];
}

@Component({
  selector: 'ziro-check-number-portability-form',
  templateUrl: './check-number-portability-form.component.html',
  standalone: false,
})
export class CheckNumberPortabilityFormComponent
  extends ZiroFormAbstractDirective<CheckNumberPortabilityComponentPayload>
  implements OnInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private translateService = inject(TranslateService);

  readonly existingPortInDraftNumbers = input<string[]>();
  @ViewChild(ExtensionRangesDisplayFormComponent) extensionRangesDisplayForm: ExtensionRangesDisplayFormComponent;
  protected readonly ZiroFormsValidationState = ZiroFormsValidationState;
  rangesFormEntity: RangeDisplayFormModel;
  extensionRangesDisplayFormValidationState: ZiroFormsValidationState;
  rangesFormOptionalValidators: ExtensionRangesOptionalValidators;
  validationStates = ZiroFormsValidationState;
  subscriptions = new Subscription();
  formConfig: ZiroFormConfig;
  overlappingExistingPortInDraftMessage = '';

  ngOnInit(): void {
    this.rangesFormOptionalValidators = {
      overlappingRangesInExistingPortInDraft: {
        existingPortInDraftNumbers: this.existingPortInDraftNumbers(),
      },
      canadianNumbersRestricted: {
        restrictedAreaCodes: CANADIAN_AREA_CODES_2024,
      },
      hasSameLengthValidation: true,
      overlappingRanges: true,
      strictE164Validation: false,
      noLeadingZeroes: true,
    };
    this.rangesFormEntity = { ranges: cloneDeep(this.entity.dialPlanRangesJson) };

    this.formConfig = {
      fields: {
        enterByType: {
          label: 'tkey;admin.order_numbers.check_number_portability.enter_by.label',
          dataAutomation: 'enter-by-type',
          componentConfig: new ZiroRadioConfig({
            buttons: [
              {
                value: EnterByType.TEXT,
                label: 'tkey;admin.order_numbers.check_number_portability.enter_by.option_1',
              },
              {
                value: EnterByType.RANGES,
                label: 'tkey;admin.order_numbers.check_number_portability.enter_by.option_2',
              },
            ],
            inline: true,
          }),
        },
        bulkNumbers: {
          label: 'tkey;admin.msdialplan.management.field.bulk_numbers.label',
          dataAutomation: 'check-number-portability-bulk-input',
          componentConfig: new ZiroTextareaConfig({
            placeholder: this.translateService.instant(
              'tkey;admin.msdialplan.management.bulk_generate.placeholder.number.portability',
            ),
          }),
          required: true,
          helpText: () => 'tkey;admin.msdialplan.management.bulk_generate.helptext.number.portability',
          validation: [
            {
              validator: (val: string) => {
                return val
                  .split(/[\n,]/)
                  .filter((num) => {
                    if (num && num.trim()) {
                      return num;
                    }
                  })
                  .every((num) => {
                    const formattedNum = num.replace(/[()\-\s]/g, '');
                    if (formattedNum.startsWith('+')) {
                      return formattedNum.match(/^\+\d{11}$/);
                    } else {
                      return formattedNum.match(/^\d{10}$/);
                    }
                  })
                  ? ZiroFormsValidationState.VALID
                  : ZiroFormsValidationState.INVALID;
              },
              message: 'tkey;validators.global.e164_strict.invalid.error',
            },
            {
              validator: (val: string) => {
                let isValid = ZiroFormsValidationState.VALID;
                const unformattedNumbers = val.replace(/\n|\r/g, ',').split(',');
                const strippedNumbers = val
                  .replace(/\n|\r/g, ',')
                  .replace(/[()\-\s]/g, '')
                  .split(',')
                  .map((num) => (num.startsWith('+1') ? num.slice(2) : num));

                this.overlappingExistingPortInDraftMessage = this.translateService.instant(
                  'tkey;admin.order_numbers.check_number_portability.number_in_existing_draft.error',
                );

                strippedNumbers.forEach((num, i) => {
                  if (this.existingPortInDraftNumbers().includes(num)) {
                    isValid = ZiroFormsValidationState.INVALID;
                    this.overlappingExistingPortInDraftMessage += `<br><strong>${unformattedNumbers[i]}</strong>`;
                  }
                });

                return isValid;
              },
              message: () => this.overlappingExistingPortInDraftMessage,
            },
            {
              validator: (inputValue: string) => {
                let isValid = ZiroFormsValidationState.VALID;
                const strippedNumbers = inputValue
                  .replace(/\n|\r/g, ',')
                  .replace(/[()\-\s]/g, '')
                  .split(',')
                  .map((num) => {
                    if (num.startsWith('+1')) {
                      return num.slice(2);
                    }
                    if (num.startsWith('1')) {
                      return num.slice(1);
                    }
                    return num;
                  });

                if (inputValue) {
                  strippedNumbers.forEach((number) => {
                    if (isValid === ZiroFormsValidationState.INVALID) {
                      return;
                    }

                    const hasCanadianAreaCode = !!CANADIAN_AREA_CODES_2024.find((areaCode) => {
                      return number.startsWith(areaCode);
                    });

                    isValid = !hasCanadianAreaCode ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
                  });
                }
                return isValid;
              },
              message: 'tkey;admin.order_numbers.check_number_portability.canadian_numbers_restricted.error',
            },
          ],
        },
      },
      options: {
        columnClasses: {
          label: 'text-lg-start col-lg-3',
          input: '',
        },
      },
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  generateBulkRanges(): DnDidRange[] {
    const generatedRanges = RangeService.generateRangesFromArray(
      this.formData['bulkNumbers']
        .replace(/\n|\r/g, ',')
        .replace(/[()\-\s]/g, '')
        .split(','),
    );
    return generatedRanges;
  }

  setDisplayFormValidationState(): void {
    this.extensionRangesDisplayFormValidationState =
      this.isFormSubmitted || this.extensionRangesDisplayForm.extensionRanges.showValidation
        ? this.extensionRangesDisplayForm.validationState
        : this.validationStates.VALID;
  }

  onRangeFormUpdate(data: ZiroFormsUpdate<RangeDisplayFormModel>): void {
    // Always update validation state, don't always update entity. Async val will fire this multiple times so entity
    // won't necessarily change onFormUpdate
    if (isEqual(this.entity.dialPlanRangesJson, data.new.ranges)) {
      this.setDisplayFormValidationState();
    } else {
      const newEntity = {
        ...cloneDeep(this.entity),
        dialPlanRangesJson: data.new.ranges,
      };

      this.entitySource.next(newEntity);

      this.setDisplayFormValidationState();
    }
  }

  submit(): Observable<null> {
    if (this.extensionRangesDisplayForm) {
      this.setDisplayFormValidationState();
    }
    return of(null);
  }
}
